// src/pages/Auth/GetStarted.js
import React, { useState, useEffect, useMemo } from 'react';
import { auth } from '../../firebase/config/config';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Star, Zap, Infinity, Loader2, ArrowLeft, User, Mail, Lock, Eye, EyeOff } from "lucide-react";
import logger from '../../utils/logger';
import { getStoredReferralCode } from '../../utils/affiliateTracker';
import './GetStarted.css';

const GetStarted = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Plan Selection, 2: Account Info, 3: Payment
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Plan definitions (matching PricingCards.tsx and LifetimeCTA.tsx)
  const plans = useMemo(() => [
    // Starter
    {
      id: 'starter_monthly',
      name: "Starter",
      price: "$24",
      priceValue: 24,
      description: "For hobbyists and creators",
      features: [
        '1 Chatbot',
        '1 Lead Generation Form',
        '1000 monthly Smart Responses',
        '20 File Uploads',
        'Train up to 30 URLs',
        'Basic Analytics',
        '3 FAQ Menu Items',
        '3 Month Conversation History',
        'Standard AI Model'
      ],
      color: "from-blue-500/20 to-blue-500/30",
      icon: <User size={20} className="text-blue-400" />,
      planType: 'subscription'
    },
    {
      id: 'starter_yearly',
      name: "Starter",
      price: "$228",
      priceValue: 228,
      description: "For hobbyists and creators",
      features: [
        '1 Chatbot',
        '1 Lead Generation Form',
        '1000 monthly Smart Responses',
        '20 File Uploads',
        'Train up to 30 URLs',
        'Basic Analytics',
        '3 FAQ Menu Items',
        '3 Month Conversation History',
        'Standard AI Model'
      ],
      color: "from-blue-500/20 to-blue-500/30",
      icon: <User size={20} className="text-blue-400" />,
      planType: 'subscription'
    },
    // Pro
    {
      id: 'pro_monthly',
      name: "Pro",
      price: "$38",
      priceValue: 38,
      description: "For growing startups",
      features: [
        '3 Chatbots',
        '10 Lead Generation Forms',
        '50,000 monthly Smart Responses',
        '100 File Uploads',
        'Train up to 1000 URLs',
        'Advanced Analytics',
        'Unlimited FAQ Menu Items',
        '1 Year Conversation History',
        'Premium AI Model',
        'White Labeling'
      ],
      popular: true,
      color: "from-purple-500/20 to-purple-500/30",
      icon: <Star size={20} className="text-purple-400" />,
      planType: 'subscription'
    },
    {
      id: 'pro_yearly',
      name: "Pro",
      price: "$360",
      priceValue: 360,
      description: "For growing startups",
      features: [
        '3 Chatbots',
        '10 Lead Generation Forms',
        '50,000 monthly Smart Responses',
        '100 File Uploads',
        'Train up to 1000 URLs',
        'Advanced Analytics',
        'Unlimited FAQ Menu Items',
        '1 Year Conversation History',
        'Premium AI Model',
        'White Labeling'
      ],
      popular: true,
      color: "from-purple-500/20 to-purple-500/30",
      icon: <Star size={20} className="text-purple-400" />,
      planType: 'subscription'
    },
    // Unlimited
    {
      id: 'unlimited_monthly',
      name: "Unlimited",
      price: "$125",
      priceValue: 125,
      description: "For high-volume brands",
      features: [
        'Unlimited Chatbots',
        'Unlimited Lead Generation Forms',
        '500,000 monthly Smart Responses',
        '500 File Uploads',
        'Train up to 5000 URLs',
        'Pro Analytics',
        'Unlimited FAQ Menu Items',
        '3 Year Conversation History',
        'Human handoff',
        'Premium AI Model'
      ],
      color: "from-orange-500/20 to-orange-500/30",
      icon: <Zap size={20} className="text-orange-400" />,
      planType: 'subscription'
    },
    {
      id: 'unlimited_yearly',
      name: "Unlimited",
      price: "$1200",
      priceValue: 1200,
      description: "For high-volume brands",
      features: [
        'Unlimited Chatbots',
        'Unlimited Lead Generation Forms',
        '500,000 monthly Smart Responses',
        '500 File Uploads',
        'Train up to 5000 URLs',
        'Pro Analytics',
        'Unlimited FAQ Menu Items',
        '3 Year Conversation History',
        'Human handoff',
        'Premium AI Model'
      ],
      color: "from-orange-500/20 to-orange-500/30",
      icon: <Zap size={20} className="text-orange-400" />,
      planType: 'subscription'
    },
    // Lifetime
    {
      id: 'lifetime',
      name: "Lifetime",
      price: "$947",
      priceValue: 947,
      description: "One-time payment for lifetime access",
      features: [
        "5 chatbots",
        "15 lead generation forms",
        "100,000 monthly smart responses",
        "250 file uploads",
        "2,000 trainable URLs",
        "Advanced analytics + CSV exports",
        "Advanced branding controls",
        "Priority support queue",
        "3 team seats",
        "Private Founders group"
      ],
      color: "from-emerald-500/20 to-green-500/30",
      icon: <Infinity size={20} className="text-emerald-400" />,
      planType: 'onetime'
    }
  ], []);

  useEffect(() => {
    // Check for pre-selected plan from pricing section
    const storedPlan = localStorage.getItem('selectedPlan');
    if (storedPlan) {
      try {
        const planData = JSON.parse(storedPlan);
        setSelectedPlan(planData);

        // If user is already logged in, go to payment, else go to account creation
        if (auth.currentUser) {
          setStep(3);
        } else {
          setStep(2);
        }
      } catch (err) {
        logger.error('Error parsing stored plan:', err);
        localStorage.removeItem('selectedPlan');
      }
    }
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && selectedPlan) {
        // If user logs in and has a selected plan, move to payment
        setStep(3);
      }
    });
    return () => unsubscribe();
  }, [selectedPlan]);

  // Get unique plans (one of each type)
  const uniquePlans = useMemo(() => {
    const planMap = new Map();
    plans.forEach(plan => {
      const baseName = plan.name;
      if (!planMap.has(baseName)) {
        planMap.set(baseName, plan);
      }
    });
    return Array.from(planMap.values());
  }, [plans]);

  // Render Plan Selection Step
  const renderPlanSelection = () => (
    <div className="plan-selection-page">
      <div className="plan-selection-header">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
        <h1 className="plan-selection-title">Choose Your Plan</h1>
        <div className="progress-indicator">
          <div className={`progress-step ${step >= 1 ? 'completed' : ''}`}>
            <div className="step-number"></div>
          </div>
          <div className={`progress-line ${step > 1 ? 'completed' : ''}`}></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="step-number"></div>
          </div>
          <div className={`progress-line ${step > 2 ? 'completed' : ''}`}></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number"></div>
          </div>
        </div>

        {/* Billing Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem' }}>
          <span style={{ fontSize: '0.875rem', fontWeight: 500, color: billingCycle === 'monthly' ? '#1f2937' : '#6b7280' }}>Monthly</span>
          <button
            onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
            style={{
              position: 'relative',
              display: 'inline-flex',
              height: '1.5rem',
              width: '2.75rem',
              alignItems: 'center',
              borderRadius: '9999px',
              transition: 'colors',
              backgroundColor: billingCycle === 'yearly' ? '#4f46e5' : '#d1d5db',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            <span style={{
              display: 'inline-block',
              height: '1rem',
              width: '1rem',
              transform: billingCycle === 'yearly' ? 'translateX(1.5rem)' : 'translateX(0.25rem)',
              borderRadius: '9999px',
              backgroundColor: 'white',
              transition: 'transform 0.2s'
            }} />
          </button>
          <span style={{ fontSize: '0.875rem', fontWeight: 500, color: billingCycle === 'yearly' ? '#1f2937' : '#6b7280' }}>
            Yearly <span style={{ color: '#10b981', fontSize: '0.75rem', marginLeft: '0.25rem' }}>(Save 20%)</span>
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
        {uniquePlans.map((basePlan) => {
          // Get the appropriate plan variant based on billing cycle
          const plan = plans.find(p =>
            p.name === basePlan.name &&
            (basePlan.planType === 'onetime' || p.id.includes(billingCycle))
          ) || basePlan;

          // Calculate monthly price for display
          const monthlyPrice = billingCycle === 'yearly' && plan.planType === 'subscription'
            ? `$${Math.round(plan.priceValue / 12)}`
            : plan.price;

          const isHighlighted = plan.popular || plan.id === 'lifetime';
          const highlightText = plan.id === 'lifetime' ? 'Limited Offer' : (plan.popular ? 'Most Popular' : null);

          return (
            <div
              key={basePlan.name}
              className={`pricing-card ${isHighlighted ? 'highlighted' : ''}`}
            >
              {highlightText && (
                <div className="highlight-badge">
                  {highlightText}
                </div>
              )}

              <div className="relative z-10 flex flex-col h-full">
                {/* Header */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <h3 className="plan-name">
                    {plan.name}
                    {billingCycle === 'yearly' && plan.planType === 'subscription' && (
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#7c3aed',
                        background: '#ede9fe',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '0.25rem',
                        marginLeft: '0.5rem'
                      }}>
                        20% off
                      </span>
                    )}
                  </h3>
                </div>

                {/* Price Section */}
                <div style={{ marginBottom: '1rem' }}>
                  <div className="price-amount">
                    {monthlyPrice}
                    <span style={{ fontSize: '1rem', fontWeight: 400, color: '#6b7280', marginLeft: '0.5rem' }}>
                      {plan.planType === 'onetime' ? '' : 'per month'}
                    </span>
                  </div>
                  <div style={{
                    fontSize: '14px',
                    lineHeight: 1.5,
                    fontWeight: 400,
                    color: '#6b7280',
                    marginTop: '0.5rem',
                    minHeight: '63px'
                  }}>
                    {plan.description}
                    {/* Yearly billing total removed for cleaner look */}
                  </div>
                </div>

                {/* Action Button - Moved before features to match Home */}
                <div style={{ marginBottom: '1rem' }}>
                  <Button
                    className="select-plan-btn"
                    onClick={() => handlePlanSelection(plan)}
                  >
                    {plan.id === 'lifetime' ? 'Get Lifetime Access' : 'Get Started'}
                  </Button>
                </div>

                {/* Features */}
                <div className="grow">
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {plan.features.slice(0, 6).map((feature, featureIndex) => (
                      <li key={featureIndex} className="feature-item">
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {plan.features.length > 6 && (
                    <button
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#0b0f1a',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        padding: '0.5rem 0',
                        textDecoration: 'underline',
                        textAlign: 'left'
                      }}
                    >
                      View all features →
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Create user in Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;

      // Create user record in backend
      const response = await fetch('/api/user/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({
          uid: user.uid,
          email: user.email,
          name: formData.name,
          plan: 'pending',
          status: 'pending_payment'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create user profile');
      }

      // Move to payment step
      setStep(3);
    } catch (err) {
      console.error("Signup error:", err);
      setError(err.message || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelection = (plan) => {
    if (plan.planType === 'custom') {
      window.location.href = '/legal/contact-us';
      return;
    }

    // Create a safe copy of the plan object without the icon component (which causes circular reference)
    const safePlan = {
      ...plan,
      icon: undefined // Remove React component
    };

    setSelectedPlan(safePlan);
    localStorage.setItem('selectedPlan', JSON.stringify(safePlan));

    if (auth.currentUser) {
      setStep(3);
    } else {
      setStep(2);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
  };

  const startLemonSqueezyCheckout = async () => {
    if (!selectedPlan) {
      setError('Please select a plan first');
      return;
    }

    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await fetch('/api/lemonsqueezy/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({
          planId: selectedPlan.id,
          referralCode: getStoredReferralCode() || undefined,
          successUrl: `${process.env.REACT_APP_BASE_URL || 'https://www.smushlabs.ai'}/payment/success`,
          cancelUrl: `${process.env.REACT_APP_BASE_URL || 'https://www.smushlabs.ai'}/payment/cancel`
        })
      });

      const data = await response.json();
      if (!response.ok || !data.url) {
        throw new Error(data.error || 'Failed to start checkout');
      }

      window.location.href = data.url;
    } catch (err) {
      logger.error('Checkout error:', err);
      setError('Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  // Render Account Info Step
  const renderAccountInfo = () => (
    <div className="account-selection-page">
      <div className="account-selection-header">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSelectedPlan(null);
            localStorage.removeItem('selectedPlan');
            setStep(1);
          }}
          className="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Plans
        </Button>
        <h1 className="account-selection-title">Create Your Account</h1>

        <div className="progress-indicator">
          <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-number"></div>
          </div>
          <div className={`progress-line ${step > 1 ? 'completed' : ''}`}></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="step-number"></div>
          </div>
          <div className={`progress-line ${step > 2 ? 'completed' : ''}`}></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number"></div>
          </div>
        </div>
      </div>

      <div className="account-form-card">
        <div className="account-form-header">
          <p className="account-form-subtitle">Let's get started! Enter your details to continue with the <strong>{selectedPlan?.name}</strong> plan</p>
        </div>

        <form onSubmit={handleSignup} className="account-form-content">
          <div className="input-row">
            <User className="input-icon" />
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Full name"
              className="unstyled-input"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="input-row">
            <Mail className="input-icon" />
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Email address"
              className="unstyled-input"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="input-row">
            <Lock className="input-icon" />
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a password"
              className="unstyled-input"
              value={formData.password}
              onChange={handleInputChange}
              required
            />
            <button type="button" className="input-toggle" onClick={() => setShowPassword(v => !v)} aria-label="Toggle password visibility">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {error && <p className="error-message">{error}</p>}

          <Button
            type="submit"
            className="w-full account-submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Continue'
            )}
          </Button>

          <div className="account-alt-signin">
            <span className="account-form-muted">Already a member?</span>{' '}
            <a href="/login" className="account-form-link">Sign in</a>
          </div>
          <p className="text-xs text-center text-muted-foreground mt-0">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </form>
      </div>
    </div>
  );

  // Render Payment Step
  const renderPayment = () => (
    <div className="account-selection-page">
      <div className="account-selection-header">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setStep(2)}
          className="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <h1 className="account-selection-title">Complete Your Purchase</h1>

        <div className="progress-indicator">
          <div className={`progress-step ${step >= 1 ? 'completed' : ''}`}>
            <div className="step-number"></div>
          </div>
          <div className={`progress-line ${step > 1 ? 'completed' : ''}`}></div>
          <div className={`progress-step ${step >= 2 ? 'completed' : ''}`}>
            <div className="step-number"></div>
          </div>
          <div className={`progress-line ${step > 2 ? 'completed' : ''}`}></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number"></div>
          </div>
        </div>
      </div>

      <div className="account-form-card">
        <div className="account-form-header">
          <p className="account-form-subtitle">You're almost there! Complete your secure payment to get started.</p>
        </div>

        <div className="payment-summary">
          <div className="summary-item">
            <span>Plan</span>
            <strong>{selectedPlan?.name}</strong>
          </div>
          <div className="summary-item">
            <span>Billing</span>
            <strong>{selectedPlan?.id?.includes('yearly') ? 'Yearly' : selectedPlan?.planType === 'onetime' ? 'One-time' : 'Monthly'}</strong>
          </div>
          <div className="summary-item" style={{ fontSize: '1.25rem', fontWeight: 'bold', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
            <span>Total</span>
            <span>{selectedPlan?.price}</span>
          </div>
        </div>

        {error && <p className="error-message">{error}</p>}

        <Button
          className="w-full account-submit"
          style={{ marginTop: '1.5rem' }}
          onClick={startLemonSqueezyCheckout}
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            'Proceed to Secure Checkout'
          )}
        </Button>

        <p className="text-xs text-center text-muted-foreground mt-4">
          Secure payment powered by Lemon Squeezy. You can cancel anytime.
        </p>
      </div>
    </div>
  );

  return (
    <div className="get-started-page">
      <div className="embossed-text">SMUSHLABS</div>
      <div className="logo-container">
        <img src="/favicon.ico" alt="SmushLabs Logo" className="logo" />
      </div>
      {step === 1 && renderPlanSelection()}
      {step === 2 && renderAccountInfo()}
      {step === 3 && renderPayment()}
    </div>
  );
};

export default GetStarted;
